import { memo } from 'react';

import { CONTENT_PACKAGE_VERSION } from '@didaskill/domain/content-packages/constants';
import type { StudentViewFull, UserViewMinimal } from '@didaskill/domain/users/types';
import { ImpulseSpinner } from '@didaskill/ui/design-system';

import { type ScormApiParams, useScormApi } from '../../hooks/scorm/useScormApi';
import * as styles from './ScormPlayer.styles';

export const VERSION_MAPPING: Record<CONTENT_PACKAGE_VERSION, '1.2' | '2004'> = {
  [CONTENT_PACKAGE_VERSION.SCORM_1_2]: '1.2',
  [CONTENT_PACKAGE_VERSION.SCORM_2004]: '2004',
};

declare global {
  interface Window {
    API?: any;
    API_1484_11?: any;
  }
}

export type ScormPlayerProps = Pick<
  ScormApiParams,
  | 'version'
  | 'onStartActivity'
  | 'onCompleteActivity'
  | 'onDataChange'
  | 'initialSuspendData'
  | 'initialAttemptId'
> & {
  user: UserViewMinimal | StudentViewFull;
  launchUrl: string;
};

export function ScormPlayerBase({
  user,
  version,
  launchUrl,
  onStartActivity = Promise.resolve,
  onCompleteActivity = Promise.resolve,
  onDataChange = Promise.resolve,
  initialSuspendData,
  initialAttemptId,
}: ScormPlayerProps) {
  const { isApiReady } = useScormApi({
    version,
    learnerId: user.id,
    learnerName: user.fullName,
    onStartActivity,
    onCompleteActivity,
    onDataChange,
    initialSuspendData,
    initialAttemptId,
  });

  if (!isApiReady) {
    return (
      <div css={styles.loader}>
        <ImpulseSpinner />
      </div>
    );
  }

  return <iframe src={launchUrl} css={styles.player} title="SCORM Content" />;
}

ScormPlayerBase.displayName = 'ScormPlayer';

export const ScormPlayer = memo(ScormPlayerBase);
